import React, { useCallback, useMemo, useState } from "react";
import { jsonToCSV } from "react-papaparse";

import { saveFile, useCSVReader, useExportCropMixComposerCommercialMixes, useTranslation, useUpdateCropMixComposerCommercialMixes } from "utils";

import {
  AdminCropMixComposerCommercialMixComponentRequest,
  AdminCropMixComposerCommercialMixRequest,
  AdminCropMixComposerCommercialMixResponse,
  CSV,
  CropRotationCompatibility,
  GetRootProps,
  StructuredError,
  UpdateCropMixComposerCommercialMixesErrorItem,
  UpdateCropMixComposerCommercialMixesResponseBody,
} from "models";

import { AdminHead, AdminTable, Button, Collapse, Flex, Icon, Modal, Statistic, Tooltip, Message as message } from "components";

type CommercialMixesCSV = {
  id: string;
  name: string;
  link: string | null;
  provider: string;
  weightProportion: number;
  seedProportion: number;
  cropId: string;
  cropName: string;
  cropRotationCompatibility: CropRotationCompatibility[];
  strategy: string | null;
};

const ErrorItem = React.memo(({ errorItem }: { errorItem: StructuredError }) => (
  <Flex vertical gap="0.5rem">
    <Flex gap="1rem">
      <strong>Type:</strong>
      <span>{errorItem.type}</span>
    </Flex>
    <Flex gap="1rem">
      <strong>Value:</strong>
      <span>"{errorItem.value}"</span>
    </Flex>
    <Flex gap="1rem">
      <strong>Expected:</strong>
      <span>{errorItem.expected}</span>
    </Flex>
    <Flex gap="1rem">
      <strong>Field:</strong>
      <span>{errorItem.field}</span>
    </Flex>
    <Flex gap="1rem">
      <strong>Message:</strong>
      <span>{errorItem.message}</span>
    </Flex>
  </Flex>
));

export const CropMixComposerCommercialMixes = () => {
  const { t } = useTranslation("practices");
  const { data: exportCropMixComposerCommercialMixesResponse, isPending } = useExportCropMixComposerCommercialMixes();
  const { mutate: updateValues, isPending: isPendingUpdate, data: updateValuesResponse } = useUpdateCropMixComposerCommercialMixes();

  const [saving, setSaving] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const { CSVReader } = useCSVReader();

  const createErrorItems = useMemo(
    () => (mixId: string, errorItems: StructuredError[]) =>
      errorItems.map((errorItem, index) => ({
        key: `${mixId}-${index}`,
        label: mixId,
        children: <ErrorItem errorItem={errorItem} />,
      })),
    [updateValuesResponse] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const CollapsableErrors = React.memo(({ errorList }: { errorList?: UpdateCropMixComposerCommercialMixesErrorItem[] }) => {
    if (!errorList || errorList.length === 0) {
      return <p>No errors</p>;
    }

    return (
      <>
        <h4>Errors listed by mix id:</h4>
        {errorList.map(({ mixId, errorItems }) => (
          <div key={mixId} style={{ marginBottom: "1rem" }}>
            <Collapse items={createErrorItems(mixId, errorItems)} />
          </div>
        ))}
      </>
    );
  });

  const UploadResultModal = React.memo(
    ({
      visible,
      onClose,
      loading,
      totalCount,
      response,
    }: {
      visible?: boolean;
      onClose: () => void;
      loading: boolean;
      totalCount?: number;
      response?: UpdateCropMixComposerCommercialMixesResponseBody;
    }) => (
      <Modal
        showModal={visible}
        onCancel={onClose}
        title="Upload Results"
        loading={loading}
        footer={null}
        closeIcon={<Icon iconName="tabler/x" />}
        width={800}
        content={
          <Flex vertical gap="2rem">
            <Flex justify="space-evenly">
              <Statistic title="Total" value={totalCount || 0} />
              <Statistic title="Success" value={response?.processedCount || 0} />
              <Statistic title="Errors" value={response?.errorCount || 0} />
            </Flex>
            {response?.errors && response.errors.length > 0 && <CollapsableErrors errorList={response.errors} />}
          </Flex>
        }
      />
    )
  );

  const formatTableData = (mix: AdminCropMixComposerCommercialMixResponse): CommercialMixesCSV[] =>
    mix.components.map((component, idx) => ({
      link: mix.link,
      id: mix.id,
      name: mix.name,
      provider: mix.provider,
      weightProportion: component.weightProportion,
      seedProportion: component.seedProportion,
      cropId: component.cropId,
      cropName: t(`crops.${component.crop.translationSlug}`),
      cropRotationCompatibility: idx === 0 ? mix.cropRotationCompatibility : [],
      strategy: idx === 0 ? mix.strategy : "",
    }));

  const formatDownloadedCSVData = (mix: AdminCropMixComposerCommercialMixResponse): CommercialMixesCSV[] =>
    mix.components.map((component, idx) => ({
      link: idx === 0 ? mix.link : "",
      id: mix.id,
      name: idx === 0 ? mix.name : "",
      provider: idx === 0 ? mix.provider : "",
      weightProportion: component.weightProportion,
      seedProportion: component.seedProportion,
      cropId: component.cropId,
      cropName: t(`crops.${component.crop.translationSlug}`),
      cropRotationCompatibility: idx === 0 ? mix.cropRotationCompatibility : [],
      strategy: idx === 0 ? mix.strategy : "",
    }));

  const tableData = useMemo(() => {
    if (!exportCropMixComposerCommercialMixesResponse) return [];

    return exportCropMixComposerCommercialMixesResponse?.flatMap(formatTableData);
  }, [exportCropMixComposerCommercialMixesResponse]); // eslint-disable-line react-hooks/exhaustive-deps

  const dataToSave = useMemo(() => {
    if (!exportCropMixComposerCommercialMixesResponse) return null;
    return exportCropMixComposerCommercialMixesResponse.flatMap(formatDownloadedCSVData);
  }, [exportCropMixComposerCommercialMixesResponse]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveCsv = useCallback(() => {
    if (!dataToSave) {
      message({ content: "Something went wrong while transforming the data.", icon: "error" });
      return;
    }

    setSaving(true);

    // Use setTimeout to prevent UI blocking
    setTimeout(() => {
      try {
        const csv = jsonToCSV(dataToSave);
        saveFile(csv, "commercial_crop_mixes.csv");
      } catch {
        message({ content: "Error generating CSV file.", icon: "error" });
      } finally {
        setSaving(false);
      }
    }, 0);
  }, [dataToSave]);

  const processFile = (csv: CSV) => {
    setSaving(true);
    setShowUploadModal(true);

    const headers = csv.data[0];
    const dataRows = csv.data.slice(1);

    const convertedData = dataRows.map((row) => {
      const rowData: {
        id: string;
        provider: string;
        name: string;
        link: string;
        cropRotationCompatibility: CropRotationCompatibility[];
        strategy: string;
        component: AdminCropMixComposerCommercialMixComponentRequest[];
      } = { id: "", provider: "", name: "", link: "", cropRotationCompatibility: [], strategy: "", component: [] };

      const component = {
        cropId: "",
        weightProportion: 0,
        seedProportion: 0,
      };

      headers.forEach((header: string, index: number) => {
        switch (header) {
          case "id":
            rowData.id = row[index] || "";
            break;
          case "cropId":
            component.cropId = row[index] || "";
            break;
          case "weightProportion":
            component.weightProportion = Number(row[index]) || 0;
            break;
          case "seedProportion":
            component.seedProportion = Number(row[index]) || 0;
            break;
          case "provider":
            rowData.provider = row[index] || "";
            break;
          case "link":
            rowData.link = row[index] || "";
            break;
          case "name":
            rowData.name = row[index] || "";
            break;
          case "cropRotationCompatibility":
            rowData.cropRotationCompatibility = row[index].split(",").map((item) => item.trim() as CropRotationCompatibility);
            break;
          case "strategy":
            rowData.strategy = row[index] || "";
            break;
          default:
            break;
        }
      });

      return { ...rowData, component: [...(rowData.component || []), component] };
    });

    const requestData = convertedData.reduce((acc, item) => {
      const existingItem = acc.find((i) => i.id === item.id);

      if (existingItem) {
        existingItem.components.push(...item.component);
      } else {
        acc.push({
          components: [...item.component],
          id: item.id,
          provider: item.provider,
          name: item.name,
          link: item.link,
          cropRotationCompatibility: item.cropRotationCompatibility,
          strategy: item.strategy,
        });
      }
      return acc;
    }, [] as AdminCropMixComposerCommercialMixRequest[]);

    // Use setTimeout to prevent UI blocking
    setTimeout(() => {
      try {
        if (requestData !== null) {
          updateValues({ data: requestData, signal: new AbortController().signal });
        }

        // if (convertedCsv.errors.length > 0) {
        //   message({ content: "CSV conversion errors occurred", icon: "error" });
        // }
      } catch {
        message({ content: "Error processing CSV file", icon: "error" });
      } finally {
        setSaving(false);
      }
    }, 0);
  };

  return (
    <>
      <AdminHead
        headline={`${exportCropMixComposerCommercialMixesResponse?.length || 0} Mixes`}
        description={
          <>
            When updating lists of properties, the CSV file that you upload must contain the same structure as the downloadable CSV (including header and
            quantity of columns). Items will be matched by ID number, which you can leave blank for new additions. Any existing items not included in your CSV
            upload will not be changed.
          </>
        }
        extra={
          <>
            <Tooltip placement="bottom" title="Download this property list">
              <Button size="small" type="secondary" disabled={saving} loading={saving} onClick={saveCsv}>
                <Icon iconName="tabler/download" size="s" />
              </Button>
            </Tooltip>
            <Tooltip placement="bottom" title="Upload property list">
              <CSVReader
                config={{
                  skipEmptyLines: true,
                }}
                noDrag
                style={{
                  dropArea: {
                    border: 0,
                    padding: 0,
                    margin: 0,
                    width: "auto",
                    height: "auto",
                    display: "inline-block",
                  },
                  progressBar: {
                    display: "none !important",
                    opacity: 0,
                  },
                }}
                onUploadAccepted={processFile}
              >
                {({ getRootProps }: { getRootProps: GetRootProps }) => (
                  <Button
                    className="margin-left-50"
                    disabled={saving}
                    dataTestId="module-access-upload-button"
                    loading={saving}
                    size="small"
                    type="secondary"
                    {...getRootProps()}
                  >
                    <Icon iconName="tabler/upload" size="s" />
                  </Button>
                )}
              </CSVReader>
            </Tooltip>
          </>
        }
      />

      {/* @ts-expect-error convert to TS */}
      <AdminTable
        loading={isPending}
        dataSource={tableData}
        rowKey="key"
        config={{
          defaultPageSize: 50, // Reduce page size for better performance
        }}
      />

      <UploadResultModal
        visible={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        loading={isPendingUpdate}
        totalCount={exportCropMixComposerCommercialMixesResponse?.length}
        response={updateValuesResponse}
      />
    </>
  );
};
